import React, { useState } from 'react';

// Facebook Pixel declaration
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacy: false,
    updates: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacy) {
      alert('Lütfen gizlilik politikasını kabul edin.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Backend API'ye contact mesajı gönder
      const response = await fetch('https://api.sobcof.com/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          privacy: formData.privacy,
          updates: formData.updates
        })
      });

      const result = await response.json();

      if (result.success) {
        // Facebook Pixel - Contact event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Contact', {
            content_name: 'Contact Form',
            content_category: formData.subject
          });
        }

        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          privacy: false,
          updates: false
        });
      } else {
        console.error('Contact submission error:', result.message);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="website-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <a href="/" className="logo-link">
              <img src="/assets/LogoBrown.png" alt="SobCof Logo" className="logo-img" />
            </a>
          </div>
          <div className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <a href="/" className="nav-link">Ana Sayfa</a>
            <a href="/#features" className="nav-link">Özellikler</a>
            <a href="/#how-it-works" className="nav-link">Nasıl Çalışır</a>
            <a href="/#download" className="nav-link">İndir</a>
            <a href="/contact" className="nav-link">İletişim</a>
            <a href="/cafe-partnership" className="nav-link">Kafe Partnerliği</a>
          </div>
          <div className="nav-toggle" onClick={toggleMobileMenu}>
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </div>
        </div>
      </nav>

      {/* Contact Content */}
      <section className="contact-content">
        <div className="container">
          <div className="contact-header">
            <h1>İletişim</h1>
            <p className="contact-subtitle">Bizimle iletişime geçin - Size en kısa sürede dönüş yapacağız</p>
          </div>

          <div className="contact-main">
            <div className="contact-form-section">
              <div className="contact-form-container">
                <h2>Bize Mesaj Gönderin</h2>
                
                {submitStatus === 'success' && (
                  <div className="alert alert-success">
                    <i className="fas fa-check-circle"></i>
                    Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="alert alert-error">
                    <i className="fas fa-exclamation-circle"></i>
                    Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                  </div>
                )}

                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">
                        <i className="fas fa-user"></i>
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Adınızı ve soyadınızı yazın"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        <i className="fas fa-envelope"></i>
                        E-posta Adresi *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">
                        <i className="fas fa-phone"></i>
                        Telefon
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+90 (555) 123 45 67"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">
                        <i className="fas fa-tag"></i>
                        Konu *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Konu seçin...</option>
                        <option value="support">🔧 Teknik Destek</option>
                        <option value="account">👤 Hesap Sorunu</option>
                        <option value="premium">👑 Premium Üyelik</option>
                        <option value="safety">🛡️ Güvenlik/Raporlama</option>
                        <option value="feedback">💡 Öneri/Geri Bildirim</option>
                        <option value="business">🤝 İş Birliği</option>
                        <option value="press">📰 Basın</option>
                        <option value="other">📋 Diğer</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">
                      <i className="fas fa-comment-alt"></i>
                      Mesajınız *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Lütfen sorununuzu, önerinizi veya mesajınızı detaylı şekilde açıklayın. Ne kadar detay verirseniz, size o kadar iyi yardımcı olabiliriz."
                    />
                    <div className="char-count">
                      {formData.message.length}/1000 karakter
                    </div>
                  </div>

                  <div className="form-agreements">
                    <div className="agreement-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          id="privacy"
                          name="privacy"
                          checked={formData.privacy}
                          onChange={handleInputChange}
                          required
                        />
                        <span className="checkmark">
                          <i className="fas fa-check"></i>
                        </span>
                        <span className="agreement-text">
                          <a href="/privacy" target="_blank" rel="noopener noreferrer">
                            Gizlilik Politikası
                          </a>'nı okudum ve kabul ediyorum *
                        </span>
                      </label>
                    </div>

                    <div className="agreement-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          id="updates"
                          name="updates"
                          checked={formData.updates}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark">
                          <i className="fas fa-check"></i>
                        </span>
                        <span className="agreement-text">
                          SobCof güncellemeleri ve haberleri hakkında e-posta almak istiyorum
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="submit-btn primary" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <div className="spinner"></div>
                          <span>Gönderiliyor...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          <span>Mesajı Gönder</span>
                        </>
                      )}
                    </button>

                    <div className="form-note">
                      <i className="fas fa-info-circle"></i>
                      <span>Mesajınıza genellikle 24 saat içinde yanıt veriyoruz</span>
                    </div>
                  </div>
                </form>
              </div>
            </div>


          </div>

          <div className="response-info">
            <div className="response-card">
              <i className="fas fa-clock"></i>
              <h3>Yanıt Süresi</h3>
              <p>E-postalarınıza genellikle 24 saat içinde yanıt veriyoruz. Acil durumlar için güvenlik e-postamızı kullanın.</p>
            </div>

            <div className="response-card">
              <i className="fas fa-language"></i>
              <h3>Dil Desteği</h3>
              <p>Türkçe ve İngilizce dillerinde destek sağlıyoruz. Mesajınızı hangi dilde yazarsanız yazın, size aynı dilde yanıt vereceğiz.</p>
            </div>

            <div className="response-card">
              <i className="fas fa-user-secret"></i>
              <h3>Gizlilik</h3>
              <p>Tüm iletişimleriniz gizli tutulur ve sadece ilgili destek ekibi üyeleri tarafından görülür.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <a href="/" className="logo-link">
                  <img src="/assets/LogoWhite.png" alt="SobCof Logo" />
                </a>
              </div>
              <p className="footer-description">
                Kahve severler için sosyal keşif platformu. En iyi kafeleri keşfet, yeni insanlarla tanış.
              </p>
              <div className="social-links">
                <a href="https://instagram.com/sobcof" title="Instagram" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
                <a href="https://tiktok.com/@sobcof" title="TikTok" target="_blank" rel="noopener noreferrer"><i className="fab fa-tiktok"></i></a>
                <a href="https://twitter.com/sobcof" title="Twitter" target="_blank" rel="noopener noreferrer"><i className="fab fa-twitter"></i></a>
                <a href="https://youtube.com/@sobcof" title="YouTube" target="_blank" rel="noopener noreferrer"><i className="fab fa-youtube"></i></a>
              </div>
            </div>
            
            <div className="footer-section">
              <h4>Ürün</h4>
              <ul>
                <li><a href="/#features">Özellikler</a></li>
                <li><a href="/#premium">Premium</a></li>
                <li><a href="/#download">İndir</a></li>
                <li><a href="/#security">Güvenlik</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Destek</h4>
              <ul>
                <li><a href="/help">Yardım Merkezi</a></li>
                <li><a href="/contact">İletişim</a></li>
                <li><a href="/feedback">Geri Bildirim</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Yasal</h4>
              <ul>
                <li><a href="/privacy">Gizlilik Politikası</a></li>
                <li><a href="/terms">Kullanım Koşulları</a></li>
                <li><a href="/cookies">Çerez Politikası</a></li>
                <li><a href="/gdpr">KVKK</a></li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2026 SobCof. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
